import { FunnelIcon } from '@heroicons/react/16/solid';
import { CheckIcon } from '@heroicons/react/24/outline';
import { DynamicForm, IDynamicFormRef, Modal } from '@src/components';
import { logFilterSchema } from '@src/formSchemas/Log/logFilterSchema';
import { applyFilteredFormData, FilterFormValue } from '@src/utilities/form';
import { Button } from 'antd';
import { useCallback, useRef, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type LogsFilterProps = {
    initialFormData?: Record<string, any>;
    onChange?: (key: string, value: FilterFormValue) => void;
    onClose?: () => void;
};

export function LogsFilter({
    initialFormData,
    onChange,
    onClose,
}: Readonly<LogsFilterProps>) {
    const { t } = useTranslation();
    const formRef = useRef<IDynamicFormRef>(null);
    const [isDirty, setIsDirty] = useState(false);

    const handleSubmit = useCallback(
        (formData: FieldValues) => {
            if (onChange) {
                applyFilteredFormData(formData, onChange);
            }
            onClose?.();
        },
        [onChange, onClose]
    );

    const handleFooterSubmit = async () => {
        if (!formRef.current) return;

        const isValid = await formRef.current.trigger();
        if (!isValid) return;

        const values = formRef.current.getValues();
        handleSubmit(values);
    };

    return (
        <Modal
            title={t('pages.logs.forms.filter.title')}
            headerIcon={<FunnelIcon width={20} height={20} />}
            isOpen={true}
            onClose={() => onClose && onClose()}
            content={
                <DynamicForm
                    ref={formRef}
                    formData={initialFormData}
                    resetFormData
                    isLoading={false}
                    formRows={logFilterSchema({ t })}
                    formKeyName="log-filter-form"
                    onHandleSubmit={handleSubmit}
                    containerClassNames="grid gap-4"
                    hasDataChanged={(value) => {
                        setIsDirty(value);
                    }}
                />
            }
            footer={
                <div className="w-full flex justify-between text-center space-x-2">
                    <Button
                        size="large"
                        className="w-full justify-cente btn-secondary"
                        htmlType="button"
                        onClick={() => onClose && onClose()}
                    >
                        {t('buttons.cancel')}
                    </Button>
                    <Button
                        size="large"
                        htmlType="submit"
                        className="w-full text-center justify-center btn-primary gap-1"
                        onClick={handleFooterSubmit}
                        disabled={!isDirty}
                        icon={<CheckIcon width={16} height={16} />}
                    >
                        {t('buttons.apply')}
                    </Button>
                </div>
            }
        />
    );
}
